import React from 'react';
import { Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DecisionFactor } from '~/types/transition';

interface DecisionFactorsCardProps {
  factors: DecisionFactor[];
}

const RESULT_CONFIG: Record<DecisionFactor['result'], { icon: React.ReactNode; color: string }> = {
  PASS: { icon: <CheckCircleIcon fontSize="small" />, color: '#2e7d32' },
  FAIL: { icon: <CancelIcon fontSize="small" />, color: '#c62828' },
  WARN: { icon: <WarningAmberIcon fontSize="small" />, color: '#e65100' },
  UNKNOWN: { icon: <HelpOutlineIcon fontSize="small" />, color: '#9e9e9e' },
};

const LABEL_STYLE = {
  fontSize: '0.65rem',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  color: 'text.secondary',
};

const DecisionFactorsCard: React.FC<DecisionFactorsCardProps> = ({ factors }) => {
  if (factors.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Decision Criteria
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...LABEL_STYLE, pl: 0, border: 0 }}>Criterion</TableCell>
            <TableCell sx={{ ...LABEL_STYLE, border: 0 }}>Patient Value</TableCell>
            <TableCell align="center" sx={{ ...LABEL_STYLE, border: 0, width: 48 }}>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {factors.map((factor) => {
            const cfg = RESULT_CONFIG[factor.result];
            return (
              <TableRow key={factor.criterion} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ pl: 0, verticalAlign: 'top' }}>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                    {factor.criterion}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                    {factor.requirement}
                  </Typography>
                </TableCell>
                <TableCell sx={{ fontSize: '0.8rem', verticalAlign: 'top' }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {factor.patientValue}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ verticalAlign: 'top', pt: 1.25 }}>
                  <Box sx={{ color: cfg.color, display: 'flex', justifyContent: 'center' }}>
                    {cfg.icon}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default DecisionFactorsCard;
